import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import ButtonBase from '@mui/material/ButtonBase';

/** Aligned with ServiceSearchFlow / provider result cards (see AGENTS.md brand style) */
export const blue = '#0d4efe';
export const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';

export const StyledContactDialog = styled(Dialog)(() => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(26, 26, 46, 0.52)',
    backdropFilter: 'blur(4px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgba(13, 78, 254, 0.14)',
    boxShadow:
      '0 24px 56px rgba(13, 78, 254, 0.16), 0 8px 24px rgba(0, 0, 0, 0.06)',
    background: '#ffffff',
    maxWidth: 480,
    width: '100%',
  },
  /* Phones: open as a bottom sheet over the results so the list keeps its place. */
  '@media (max-width: 600px)': {
    '& .MuiDialog-container': {
      alignItems: 'flex-end',
    },
    '& .MuiDialog-paper': {
      margin: 0,
      maxWidth: '100%',
      maxHeight: '92vh',
      borderRadius: '16px 16px 0 0',
    },
  },
}));

export const ContactModalTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.75),
  margin: 0,
  padding: theme.spacing(2.5, 3),
  paddingRight: theme.spacing(6),
  paddingBottom: theme.spacing(2),
  position: 'relative',
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.14) 0%, rgba(13, 78, 254, 0.1) 55%, rgba(13, 78, 254, 0.04) 100%)',
  borderBottom: '1px solid rgba(13, 78, 254, 0.1)',
  fontWeight: 700,
  fontSize: '1.125rem',
  lineHeight: 1.35,
  letterSpacing: '-0.02em',
  color: ink,
  '& .contact-modal-icon-wrap': {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background:
      'linear-gradient(145deg, rgba(1, 235, 178, 0.2) 0%, rgba(13, 78, 254, 0.15) 100%)',
    border: '1px solid rgba(13, 78, 254, 0.18)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
  },
  '& .contact-modal-icon': {
    fontSize: 26,
    color: blue,
  },
}));

export const ContactModalCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: muted,
  borderRadius: 10,
  '&:hover': {
    background: 'rgba(13, 78, 254, 0.08)',
    color: ink,
  },
}));

export const ContactModalContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(0, 3, 2.5),
  paddingTop: theme.spacing(2),
  '&.MuiDialogContent-root': {
    paddingTop: theme.spacing(2),
  },
  color: muted,
  fontSize: '0.95rem',
  lineHeight: 1.65,
}));

export const ContactLeadText = styled('p')({
  margin: '0 0 16px',
  fontSize: '0.95rem',
  lineHeight: 1.6,
  color: muted,
});

/** Service / subservice / urgency reference (matches guest form panel tone) */
export const ContactSearchReferencePanel = styled(Box)(() => ({
  marginBottom: '16px',
  padding: '14px 16px',
  borderRadius: '12px',
  background:
    'linear-gradient(135deg, rgba(13, 78, 254, 0.05) 0%, rgba(1, 235, 178, 0.04) 100%)',
  border: '1px solid rgba(13, 78, 254, 0.12)',
}));

/** Clickable wrapper — opens full wizard-style summary */
export const ContactSearchReferencePanelTrigger = styled(ButtonBase)(() => ({
  display: 'block',
  width: '100%',
  marginBottom: '16px',
  padding: '14px 16px',
  borderRadius: '12px',
  textAlign: 'left',
  background:
    'linear-gradient(135deg, rgba(13, 78, 254, 0.05) 0%, rgba(1, 235, 178, 0.04) 100%)',
  border: '1px solid rgba(13, 78, 254, 0.12)',
  transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.12s ease',
  '&:hover': {
    borderColor: 'rgba(13, 78, 254, 0.28)',
    boxShadow: '0 4px 16px rgba(13, 78, 254, 0.1)',
  },
  '&:focus-visible': {
    outline: `2px solid ${blue}`,
    outlineOffset: 2,
  },
}));

export const ContactSearchReferenceTitle = styled('div')(() => ({
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#8a94a6',
  marginBottom: '10px',
}));

export const ContactSearchRefRow = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '92px 1fr',
  gap: '8px 12px',
  fontSize: '0.875rem',
  lineHeight: 1.45,
  '& + &': {
    marginTop: '8px',
  },
  '@media (max-width: 400px)': {
    gridTemplateColumns: '1fr',
    gap: '2px',
  },
}));

export const ContactSearchRefLabel = styled('span')(() => ({
  fontWeight: 600,
  color: ink,
}));

export const ContactSearchRefValue = styled('span')(() => ({
  color: muted,
}));

export const GuestFormSection = styled(Box)(() => ({
  marginTop: 4,
  padding: '18px 16px 20px',
  borderRadius: 12,
  background: '#f7fafc',
  border: '1px solid rgba(0, 0, 0, 0.06)',
}));

export const GuestFormGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    '& .guest-form-full-row': {
      gridColumn: '1 / -1',
    },
  },
}));

export const ContactField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    backgroundColor: '#fff',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.1)',
    borderWidth: '1.5px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(13, 78, 254, 0.35)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: blue,
    borderWidth: '1.5px',
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    boxShadow: '0 0 0 3px rgba(13, 78, 254, 0.08)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: blue,
  },
}));

export const ContactModalActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(0, 3, 2.75),
  gap: theme.spacing(1.25),
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  background: 'linear-gradient(180deg, rgba(247, 250, 252, 0.9) 0%, #f7fafc 100%)',
  borderTop: '1px solid rgba(0, 0, 0, 0.06)',
}));

export const CancelModalButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  padding: '10px 20px',
  color: ink,
  borderColor: 'rgba(13, 78, 254, 0.28)',
  backgroundColor: '#fff',
  '&:hover': {
    borderColor: blue,
    backgroundColor: 'rgba(13, 78, 254, 0.06)',
  },
}));

export const SendModalButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  padding: '10px 22px',
  color: '#fff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  '&:hover': {
    background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
    opacity: 0.93,
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.42)',
  },
  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(255, 255, 255, 0.65)',
    boxShadow: 'none',
    opacity: 1,
  },
}));

export const ContactDisclaimerText = styled('p')(() => ({
  margin: '14px 0 0',
  fontSize: '0.78rem',
  lineHeight: 1.5,
  color: muted,
  '& a': {
    color: blue,
    fontWeight: 600,
    textDecoration: 'none',
  },
  '& a:hover': {
    textDecoration: 'underline',
  },
}));

export const ContactErrorAlert = styled(Alert)(() => ({
  borderRadius: 10,
  marginBottom: 16,
}));

export const ContactMethodSection = styled(Box)(() => ({
  marginTop: 16,
  marginBottom: 4,
  padding: '16px',
  borderRadius: 12,
  background:
    'linear-gradient(135deg, rgba(13, 78, 254, 0.05) 0%, rgba(1, 235, 178, 0.04) 100%)',
  border: '1px solid rgba(13, 78, 254, 0.12)',
}));

export const ContactMethodLabel = styled('div')(() => ({
  fontSize: '0.9rem',
  fontWeight: 700,
  color: ink,
  marginBottom: 12,
  letterSpacing: '-0.005em',
}));

export const ContactMethodOptions = styled('div')(() => ({
  display: 'flex',
  gap: 10,
}));

export const ContactMethodSavedNote = styled('p')(() => ({
  margin: 0,
  fontSize: '0.9rem',
  lineHeight: 1.5,
  color: ink,
  fontWeight: 500,
  '& + &': {
    marginTop: 8,
    fontSize: '0.8rem',
    fontWeight: 400,
    color: muted,
  },
  '& strong': {
    fontWeight: 800,
    color: ink,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  '& a': {
    color: blue,
    fontWeight: 600,
    textDecoration: 'none',
  },
  '& a:hover': {
    textDecoration: 'underline',
  },
}));

export const ContactMethodButton = styled('button')(({ $selected }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '12px 14px',
  borderRadius: 10,
  fontSize: '0.9rem',
  fontWeight: 700,
  cursor: 'pointer',
  border: `2px solid ${$selected ? blue : 'rgba(0, 0, 0, 0.14)'}`,
  background: $selected
    ? `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`
    : '#fff',
  color: $selected ? '#fff' : ink,
  boxShadow: $selected ? '0 4px 14px rgba(13, 78, 254, 0.3)' : 'none',
  transition:
    'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, color 0.15s ease',
  '& svg': {
    fontSize: 19,
  },
  '&:hover:not(:disabled)': {
    borderColor: $selected ? blue : 'rgba(13, 78, 254, 0.4)',
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: $selected ? 1 : 0.45,
  },
}));

/** Guest email matches an existing account — sign-in callout (teal/blue brand panel) */
export const ContactExistingAccountPanel = styled(Box)(() => ({
  display: 'flex',
  gap: '14px',
  alignItems: 'flex-start',
  marginBottom: 16,
  padding: '16px 18px',
  borderRadius: 12,
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.12) 0%, rgba(13, 78, 254, 0.08) 55%, rgba(13, 78, 254, 0.04) 100%)',
  border: '1px solid rgba(13, 78, 254, 0.18)',
}));

export const ContactExistingAccountIconWrap = styled(Box)(() => ({
  flexShrink: 0,
  width: 40,
  height: 40,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'linear-gradient(145deg, rgba(1, 235, 178, 0.2) 0%, rgba(13, 78, 254, 0.15) 100%)',
  border: '1px solid rgba(13, 78, 254, 0.18)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
  '& .MuiSvgIcon-root': {
    fontSize: 22,
    color: blue,
  },
}));

export const ContactExistingAccountBody = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
}));

export const ContactExistingAccountTitle = styled('div')(() => ({
  fontSize: '0.9rem',
  fontWeight: 700,
  lineHeight: 1.35,
  color: ink,
  marginBottom: 6,
  letterSpacing: '-0.01em',
}));

export const ContactExistingAccountMessage = styled('p')(() => ({
  margin: 0,
  fontSize: '0.875rem',
  lineHeight: 1.55,
  color: muted,
}));

export const ContactSignInButton = styled(Button)(() => ({
  marginTop: 12,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  padding: '8px 18px',
  fontSize: '0.875rem',
  color: '#fff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  '&:hover': {
    background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
    opacity: 0.93,
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.42)',
  },
}));

/** Timing / contact choice chips that wrap on narrow sheets */
export const ContactChoiceOptions = styled('div')(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: 8,
}));

export const ContactSheetSection = styled(Box)(() => ({
  marginTop: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
}));

export const AlsoSendPanel = styled(Box)(() => ({
  marginTop: 16,
  padding: '10px 14px',
  borderRadius: 12,
  background: '#f7fafc',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9rem',
    color: ink,
    lineHeight: 1.4,
  },
  '& .MuiCheckbox-root.Mui-checked': {
    color: blue,
  },
}));

export const AlsoSendNames = styled('span')(() => ({
  display: 'block',
  fontSize: '0.8rem',
  color: muted,
}));

export const ConfirmationHero = styled(Box)(() => ({
  display: 'flex',
  gap: 14,
  alignItems: 'flex-start',
  padding: '16px 18px',
  borderRadius: 12,
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.12) 0%, rgba(13, 78, 254, 0.08) 55%, rgba(13, 78, 254, 0.04) 100%)',
  border: '1px solid rgba(13, 78, 254, 0.18)',
  '& .MuiSvgIcon-root': {
    fontSize: 30,
    color: '#16a34a',
    flexShrink: 0,
  },
}));

export const ConfirmationTitle = styled('div')(() => ({
  fontSize: '1rem',
  fontWeight: 700,
  color: ink,
  marginBottom: 4,
}));

export const ConfirmationText = styled('p')(() => ({
  margin: 0,
  fontSize: '0.9rem',
  lineHeight: 1.55,
  color: muted,
}));

export const ConfirmationDetails = styled(Box)(() => ({
  marginTop: 16,
  padding: '14px 16px',
  borderRadius: 12,
  background: '#f7fafc',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  '& a': {
    color: blue,
    fontWeight: 700,
    textDecoration: 'none',
  },
  '& a:hover': {
    textDecoration: 'underline',
  },
}));

export const ConfirmationList = styled('ul')(() => ({
  margin: '6px 0 0',
  paddingLeft: 18,
  fontSize: '0.875rem',
  color: muted,
  lineHeight: 1.6,
}));
